import React, { useMemo, useState } from 'react';
import { Dropdown, Form, Icon, Label, Message } from 'semantic-ui-react';
import {
  Table, HeaderCell, TableBody, TableCell, TableHeader, TableRow
} from '../../../../common/components/Table';
import { Card } from 'common/components/Card';
import { PageSection, Subtitle } from 'common/styles/page';

import OrganizationSurveyStructureModal from './OrganizationSurveyStructureModal';
import OrganizationSurveyStructureAddCategories from './OrganizationSurveyStructureAddCategories';
import { StyledAddButton } from 'common/components/button/StyledButton';
import { surveyQuestionApi, SurveyType } from '../api/surveyQuestionApi';
import LoadingSpinner from 'common/components/LoadingSpinner';
import { QuestionStatus } from 'areas/settings/survey-questions/constants/SurveyQuestionTypes';
import { OrganizationCategory, OrgFormValue } from './OrganizationEdit';

interface Props {
  organization: OrgFormValue | null;
  onOrganizationChange: (updated: any) => void;
  organizationId: number;
}

const OrganizationSurveyStructure: React.FC<Props> = ({
  organization,
  onOrganizationChange,
  organizationId,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState<OrganizationCategory | null>(null);
  const [addCategoryModalOpen, setAddCategoryModalOpen] = useState(false);

  const { data: questions = [], isLoading } =
    surveyQuestionApi.useApiAdminV1SurveyQuestionListQuery({});

  const surveyQuestionOptions = questions
    .filter(q => q.survey_type === SurveyType.EDI && q.status === QuestionStatus.ACTIVE)
    .map(q => ({ key: q.id, value: q.id, text: q.name }));

  const outcomeQuestionOptions = questions
    .filter(q => q.survey_type === SurveyType.Outcomes && q.status === QuestionStatus.ACTIVE)
    .map(q => ({ key: q.id, value: q.id, text: q.name }));

  // prefer new org fields; fall back to legacy *_id if still present
  const currentSurveyQuestionId =
    organization?.default_survey_question ?? organization?.default_survey_question ?? null;
  const currentOutcomeQuestionId =
    organization?.default_outcome_question ?? organization?.default_outcome_question ?? null;

  const handleDeleteCategory = (categoryId: string) => {
    const updated = (organization?.categories || []).filter(cat => cat.id !== categoryId);
    onOrganizationChange({ ...organization, categories: updated });
  };

  const sortedCategories = useMemo(() => {
    const categories = Array.isArray(organization?.categories) ? [...organization.categories] : [];
    const priorityRank = (priority: string | undefined) =>
      priority === 'primary' ? 1 : priority === 'secondary' ? 2 : 3;

    return categories.sort((a, b) => {
      const rankDiff = priorityRank(a.priority) - priorityRank(b.priority);
      if (rankDiff !== 0) return rankDiff;
      return String(a.name).localeCompare(String(b.name));
    });
  }, [organization?.categories]);

  if (isLoading) return <LoadingSpinner />;

  return (
    <PageSection>
      <PageSection padbottom>
        <Card>
          <Form>
            <Form.Group widths="equal" style={{ columnGap: '2rem' }}>
              <Form.Field style={{ display: 'flex', alignItems: 'center' }}>
                <label style={{ minWidth: 180, marginRight: '1rem' }}>Survey Questions</label>
                <Dropdown
                  placeholder="Select Survey Question"
                  selection
                  options={surveyQuestionOptions}
                  style={{ minWidth: 200 }}
                  value={currentSurveyQuestionId ?? undefined}
                  onChange={(_, { value }) => {
                    onOrganizationChange({
                      ...organization,
                      default_survey_question: value == null ? null : Number(value),
                    });
                  }}
                  loading={isLoading}
                  disabled={isLoading}
                />
              </Form.Field>

              <Form.Field style={{ display: 'flex', alignItems: 'center' }}>
                <label style={{ minWidth: 180, marginRight: '1rem' }}>Outcome Questions</label>
                <Dropdown
                  placeholder="Select Outcome Question"
                  selection
                  options={outcomeQuestionOptions}
                  style={{ minWidth: 200 }}
                  value={currentOutcomeQuestionId ?? undefined}
                  onChange={(_, { value }) => {
                    onOrganizationChange({
                      ...organization,
                      default_outcome_question: value == null ? null : Number(value),
                    });
                  }}
                  loading={isLoading}
                  disabled={isLoading}
                />
              </Form.Field>
            </Form.Group>

            <Form.Group widths="equal" style={{ columnGap: '2rem' }}>
              <Form.Field style={{ display: 'flex', alignItems: 'center' }}>
                <label style={{ minWidth: 180, marginRight: '1rem' }}>Low Score Follow-up</label>
                <input
                  type="number"
                  min="0"
                  style={{ width: 80 }}
                  value={organization?.default_min_followup ?? ''}
                  onChange={e => {
                    const v = e.target.value;
                    onOrganizationChange({
                      ...organization,
                      default_min_followup: v === '' ? null : Number(v),
                    });
                  }}
                />
              </Form.Field>

              <Form.Field style={{ display: 'flex', alignItems: 'center' }}>
                <label style={{ minWidth: 180, marginRight: '1rem' }}>High Score Follow-up</label>
                <input
                  type="number"
                  min="0"
                  style={{ width: 80 }}
                  value={organization?.default_max_followup ?? ''}
                  onChange={e => {
                    const v = e.target.value;
                    onOrganizationChange({
                      ...organization,
                      default_max_followup: v === '' ? null : Number(v),
                    });
                  }}
                />
              </Form.Field>
            </Form.Group>
          </Form>
        </Card>
      </PageSection>

      <PageSection display="flex" justifyContent="space-between" alignItems="center" padbottom="15px" padtop>
        <Subtitle>Current Categories</Subtitle>
        <StyledAddButton
          label="Select Categories"
          width="160px"
          onClick={() => setAddCategoryModalOpen(true)}
        />
      </PageSection>

      {organization && (
        <PageSection>
          <Table>
            <TableHeader>
              <TableRow>
                <HeaderCell width={4}>Category</HeaderCell>
                <HeaderCell width={3}>Type</HeaderCell>
                <HeaderCell width={3}>Visibility</HeaderCell>
                <HeaderCell width={4}>Survey Prompt</HeaderCell>
                <HeaderCell width={2} justify="center">Actions</HeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedCategories.map((category: OrganizationCategory) => (
                <TableRow
                  key={category.id}
                  $selectable
                  onClick={() => {
                    setCategoryToEdit(category);
                    setModalOpen(true);
                  }}
                >
                  <TableCell $firstcol>
                    <strong>{category.name}</strong>
                  </TableCell>

                  <TableCell>
                    <Label color={
                      category.priority === 'primary' ? 'blue'
                      : category.priority === 'secondary' ? 'yellow'
                      : 'grey'
                    }>
                      {category.priority === 'primary' ? 'Primary'
                      : category.priority === 'secondary' ? 'Custom'
                      : 'nuLogic'}
                    </Label>
                  </TableCell>

                  <TableCell>
                    <Label color={category.visibility === 'public' ? 'green' : 'blue'}>
                      {category.visibility === 'public' ? 'Public' : 'Private'}
                    </Label>
                  </TableCell>

                  <TableCell>
                    <Label>
                      <Icon
                        name={
                          category.input === 'never_ask'
                            ? 'times circle'
                            : category.input === 'always_ask'
                            ? 'check circle'
                            : 'question circle'
                        }
                        color={
                          category.input === 'never_ask'
                            ? 'red'
                            : category.input === 'always_ask'
                            ? 'green'
                            : 'blue'
                        }
                      />
                      {category.input === 'never_ask'
                        ? 'Never ask'
                        : category.input === 'always_ask'
                        ? 'Always ask'
                        : 'Ask if not found'}
                    </Label>
                  </TableCell>

                  <TableCell width={2} justify="center">
                    <Dropdown
                      button
                      icon="ellipsis vertical"
                      floating
                      className="icon"
                      direction="left"
                      style={{ width: '38px', height: '35px', borderRadius: '8px' }}
                      onClick={e => e.stopPropagation()}
                    >
                      <Dropdown.Menu>
                        <Dropdown.Item
                          text="Edit Category"
                          onClick={e => {
                            e.stopPropagation();
                            setCategoryToEdit(category);
                            setModalOpen(true);
                          }}
                        />
                        <Dropdown.Item
                          text="Delete Category"
                          onClick={e => {
                            e.stopPropagation();
                            handleDeleteCategory(category.id);
                          }}
                        />
                      </Dropdown.Menu>
                    </Dropdown>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </PageSection>
      )}

      <OrganizationSurveyStructureModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setCategoryToEdit(null);
        }}
        category={categoryToEdit ?? undefined}
        onSaveCategory={(updatedCategory) => {
          const merged = (organization?.categories || []).map((cat: any) => {
            if (cat.id !== updatedCategory.id) return cat;

            return {
              ...cat,
              ...updatedCategory,
              visibility: updatedCategory.visibility ?? cat.visibility ?? 'private',
            };
          });

          onOrganizationChange({ ...organization, categories: merged });
        }}
      />

      <OrganizationSurveyStructureAddCategories
        open={addCategoryModalOpen}
        onClose={() => setAddCategoryModalOpen(false)}
        organizationId={organizationId}
        onCategoriesUpdated={(newCategories) => {
          const updated = [...(organization?.categories || []), ...newCategories];
          onOrganizationChange({ ...organization, categories: updated });
        }}
        existingCategoryNames={(organization?.categories || []).map((c: any) => c.name)}
      />

      {!organization && (
        <Message info>
          <Message.Header>No Survey Settings Loaded</Message.Header>
          <p>There are no survey settings available for this organization.</p>
        </Message>
      )}
    </PageSection>
  );
};

export default OrganizationSurveyStructure;
